import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import {
  API_USER_SETTINGS_ADS,
  API_USER_SETTINGS_COMMERCIAL_NEWS,
} from '@/api/endpoints';
import { USER_QUERY_KEY } from '@/api/queryKeys';
import { UserSetting, type UserInfoResponse } from '@/types/user';
import { ErrorMessage, logRequestError } from '@/utils/errors';

type SettingField = 'isAdvertisingEnabled' | 'isCommercialNewsSubscriber';

const settingConfig: Record<
  UserSetting,
  { endpoint: string; field: SettingField; errorMessage: ErrorMessage }
> = {
  [UserSetting.Advertising]: {
    endpoint: API_USER_SETTINGS_ADS,
    field: 'isAdvertisingEnabled',
    errorMessage: ErrorMessage.ChangeAdvertising,
  },
  [UserSetting.CommercialNews]: {
    endpoint: API_USER_SETTINGS_COMMERCIAL_NEWS,
    field: 'isCommercialNewsSubscriber',
    errorMessage: ErrorMessage.ChangeCommercialNews,
  },
};

interface Variables {
  setting: UserSetting;
  value: boolean;
}

interface Params {
  onError?: () => void;
}

export const useUserSettings = ({ onError }: Params = {}) => {
  const queryClient = useQueryClient();

  const { mutate: updateSetting } = useMutation({
    mutationFn: ({ setting, value }: Variables) => {
      const { endpoint, field } = settingConfig[setting];

      return apiClient.post(endpoint, { [field]: value });
    },
    onMutate: async ({ setting, value }) => {
      const { field } = settingConfig[setting];

      await queryClient.cancelQueries({ queryKey: USER_QUERY_KEY });
      const previousUser =
        queryClient.getQueryData<UserInfoResponse>(USER_QUERY_KEY);

      queryClient.setQueryData<UserInfoResponse>(USER_QUERY_KEY, (user) =>
        user
          ? { ...user, userSettings: { ...user.userSettings, [field]: value } }
          : user,
      );

      return { previousUser };
    },
    onError: (error, { setting }, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(USER_QUERY_KEY, context.previousUser);
      }

      logRequestError(settingConfig[setting].errorMessage, error);
      onError?.();
    },
  });

  return { updateSetting };
};
